import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import ReplyIcon from "@material-ui/icons/Reply";

import ContactAvatar from "../ContactAvatar";
import useNow from "../../hooks/useNow";
import { replyContactName, useReplyNotifications } from "../../context/ReplyNotifications";
import { replyCountLabel, replyTimeLabel } from "../../utils/replyNotifications";
import { OPEN_REPLY_NOTIFICATIONS_EVENT } from "../NotificationsPopOver";

const VISIBLE_CARDS = 3;
const SUBTLE_MS = 6000;

const useStyles = makeStyles(theme => {
	const dark = theme.palette.type === "dark";
	return {
		"@keyframes rnSlide": {
			from: { opacity: 0, transform: "translateX(24px)" },
			to: { opacity: 1, transform: "translateX(0)" },
		},
		"@keyframes rnDrop": {
			from: { opacity: 0, transform: "translate(-50%, -10px)" },
			to: { opacity: 1, transform: "translate(-50%, 0)" },
		},
		"@keyframes rnTimer": {
			from: { transform: "scaleX(1)" },
			to: { transform: "scaleX(0)" },
		},
		stack: {
			position: "fixed",
			top: 76,
			right: 16,
			zIndex: theme.zIndex.snackbar,
			display: "flex",
			flexDirection: "column",
			gap: 10,
			width: 360,
			maxWidth: "calc(100vw - 32px)",
			pointerEvents: "none",
			[theme.breakpoints.down("xs")]: { top: 64, right: 12, maxWidth: "calc(100vw - 24px)" },
		},
		card: {
			position: "relative",
			display: "flex",
			alignItems: "flex-start",
			gap: 12,
			padding: "14px 40px 14px 14px",
			borderRadius: 14,
			border: `1px solid ${theme.custom.border}`,
			borderLeft: `4px solid ${theme.palette.primary.main}`,
			backgroundColor: theme.custom.surface,
			color: theme.palette.text.primary,
			boxShadow: dark ? "0 16px 40px rgba(0,0,0,.45)" : "0 16px 40px rgba(15,23,42,.16)",
			cursor: "pointer",
			pointerEvents: "auto",
			textAlign: "left",
			font: "inherit",
			width: "100%",
			animation: "$rnSlide .28s cubic-bezier(.2,.9,.3,1) both",
			"&:hover": { backgroundColor: theme.custom.surfaceHover },
			"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
		},
		avatar: { width: 42, height: 42, flexShrink: 0 },
		body: { flex: 1, minWidth: 0 },
		kicker: {
			display: "flex",
			alignItems: "center",
			gap: 4,
			fontSize: ".68rem",
			fontWeight: 800,
			letterSpacing: ".05em",
			textTransform: "uppercase",
			color: dark ? "#6EE7B7" : theme.palette.primary.main,
			"& svg": { fontSize: 14 },
		},
		name: {
			fontWeight: 800,
			fontSize: ".92rem",
			marginTop: 2,
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
		preview: {
			fontSize: ".82rem",
			lineHeight: 1.45,
			color: theme.palette.text.secondary,
			marginTop: 2,
			display: "-webkit-box",
			WebkitLineClamp: 2,
			WebkitBoxOrient: "vertical",
			overflow: "hidden",
			overflowWrap: "anywhere",
		},
		meta: { display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: ".7rem", color: theme.palette.text.secondary },
		count: {
			fontWeight: 800,
			color: "#fff",
			backgroundColor: theme.palette.primary.main,
			borderRadius: 999,
			padding: "1px 7px",
		},
		close: {
			position: "absolute",
			top: 6,
			right: 6,
			padding: 5,
			color: theme.palette.text.secondary,
			"& svg": { fontSize: 18 },
		},
		more: {
			alignSelf: "flex-end",
			pointerEvents: "auto",
			border: `1px solid ${theme.custom.border}`,
			backgroundColor: theme.custom.surface,
			color: theme.palette.text.primary,
			borderRadius: 999,
			padding: "6px 12px",
			fontSize: ".76rem",
			fontWeight: 700,
			cursor: "pointer",
			boxShadow: dark ? "0 8px 20px rgba(0,0,0,.35)" : "0 8px 20px rgba(15,23,42,.10)",
			"&:hover": { backgroundColor: theme.custom.surfaceHover },
		},
		// Discreto: no centro da barra superior, longe do campo de digitação e
		// dos botões do atendimento aberto.
		subtle: {
			position: "fixed",
			top: 8,
			left: "50%",
			transform: "translateX(-50%)",
			zIndex: theme.zIndex.snackbar,
			display: "flex",
			alignItems: "center",
			gap: 10,
			width: "max-content",
			maxWidth: "min(420px, calc(100vw - 40px))",
			padding: "8px 34px 8px 8px",
			borderRadius: 999,
			border: `1px solid ${theme.custom.border}`,
			backgroundColor: theme.custom.surface,
			color: theme.palette.text.primary,
			boxShadow: dark ? "0 10px 28px rgba(0,0,0,.4)" : "0 10px 28px rgba(15,23,42,.12)",
			cursor: "pointer",
			overflow: "hidden",
			font: "inherit",
			textAlign: "left",
			opacity: 0.97,
			animation: "$rnDrop .25s ease-out both",
			"&:hover": { backgroundColor: theme.custom.surfaceHover },
			"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
			[theme.breakpoints.down("xs")]: { top: 62, maxWidth: "calc(100vw - 24px)" },
		},
		subtleAvatar: { width: 30, height: 30, fontSize: ".72rem", flexShrink: 0 },
		subtleText: {
			minWidth: 0,
			fontSize: ".8rem",
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
			color: theme.palette.text.secondary,
			"& b": { color: theme.palette.text.primary, fontWeight: 700 },
		},
		subtleClose: {
			position: "absolute",
			right: 4,
			top: "50%",
			marginTop: -13,
			padding: 4,
			color: theme.palette.text.secondary,
			"& svg": { fontSize: 16 },
		},
		subtleTimer: {
			position: "absolute",
			left: 0,
			right: 0,
			bottom: 0,
			height: 2,
			backgroundColor: theme.palette.primary.main,
			opacity: 0.5,
			transformOrigin: "left center",
			animation: `$rnTimer ${SUBTLE_MS}ms linear forwards`,
		},
	};
});

const ReplyNotificationToasts = () => {
	const classes = useStyles();
	const { cards, subtle, openNotification, dismissCard, dismissSubtle } = useReplyNotifications();
	const now = useNow(cards.length > 0);

	useEffect(() => {
		if (!subtle) return undefined;
		const timer = setTimeout(dismissSubtle, SUBTLE_MS);
		return () => clearTimeout(timer);
	}, [subtle?.key, dismissSubtle]); // eslint-disable-line react-hooks/exhaustive-deps

	const visibleCards = cards.slice(0, VISIBLE_CARDS);
	const hidden = cards.length - visibleCards.length;

	return (
		<>
			{cards.length > 0 && (
				<div className={classes.stack} role="region" aria-label="Novas respostas de clientes" aria-live="polite">
					{visibleCards.map(notification => {
						const name = replyContactName(notification);
						const count = replyCountLabel(notification);
						return (
							<div
								key={notification.ticketId}
								role="button"
								tabIndex={0}
								className={classes.card}
								onClick={() => openNotification(notification)}
								onKeyDown={event => {
									if (event.key === "Enter" || event.key === " ") {
										event.preventDefault();
										openNotification(notification);
									}
								}}
								aria-label={`${name} respondeu: ${notification.lastBody || "nova mensagem"}. Abrir conversa`}
							>
								<ContactAvatar contact={notification.contact || { name: notification.contactName }} className={classes.avatar} />
								<div className={classes.body}>
									<div className={classes.kicker}><ReplyIcon />Cliente respondeu</div>
									<Typography className={classes.name}>{name}</Typography>
									<div className={classes.preview}>{notification.lastBody || "Nova mensagem"}</div>
									<div className={classes.meta}>
										{count && <span className={classes.count}>{count}</span>}
										<span>{replyTimeLabel(notification.notifiedAt, now)} · clique para abrir</span>
									</div>
								</div>
								<IconButton
									className={classes.close}
									aria-label="Fechar aviso (continua no sino de notificações)"
									title="Fechar aviso (continua no sino)"
									onClick={event => {
										event.stopPropagation();
										dismissCard(notification.ticketId);
									}}
								>
									<CloseIcon />
								</IconButton>
							</div>
						);
					})}
					{hidden > 0 && (
						<button
							type="button"
							className={classes.more}
							onClick={() => window.dispatchEvent(new Event(OPEN_REPLY_NOTIFICATIONS_EVENT))}
						>
							+{hidden} {hidden === 1 ? "outra resposta" : "outras respostas"}
						</button>
					)}
				</div>
			)}
			{subtle && (
				<div
					key={subtle.key}
					role="status"
					aria-live="polite"
					tabIndex={0}
					className={classes.subtle}
					onClick={() => openNotification(subtle.notification)}
					onKeyDown={event => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							openNotification(subtle.notification);
						}
					}}
					aria-label={`${replyContactName(subtle.notification)} respondeu. Abrir conversa`}
				>
					<ContactAvatar
						contact={subtle.notification.contact || { name: subtle.notification.contactName }}
						className={classes.subtleAvatar}
					/>
					<span className={classes.subtleText}>
						<b>{replyContactName(subtle.notification)}</b> respondeu: {subtle.notification.lastBody || "nova mensagem"}
					</span>
					<IconButton
						className={classes.subtleClose}
						aria-label="Fechar aviso"
						onClick={event => {
							event.stopPropagation();
							dismissSubtle();
						}}
					>
						<CloseIcon />
					</IconButton>
					<span className={classes.subtleTimer} aria-hidden="true" />
				</div>
			)}
		</>
	);
};

export default ReplyNotificationToasts;
